import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCurrentApiEndpoints } from '../../config/api';

const FormContainer = styled.div`
  background: white;
  padding: 2.5rem;
  border-radius: 20px;
  border: 2px solid #000;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  max-width: 400px;
  margin: 2rem auto;
  width: 100%;
  box-sizing: border-box;
`;

const Title = styled.h1`
  text-align: center;
  color: #333;
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
`;

const Description = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const Button = styled.button`
  background: #000;
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  
  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1rem;
  border-radius: 25px;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 1rem;
  border-radius: 25px;
  margin-bottom: 1rem;
`;

const PasswordStrength = styled.div<{ strength: number }>`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: ${props => {
    if (props.strength < 2) return '#dc3545';
    if (props.strength < 3) return '#ffc107';
    return '#28a745';
  }};
`;

const BackToLogin = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  
  a {
    color: #667eea;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid reset link. Please request a new password reset.');
        setIsVerifying(false);
        return;
      }

      try {
        const endpoints = getCurrentApiEndpoints();
        const response = await axios.post(endpoints.VERIFY_RESET_TOKEN, {
          token: token
        });
        
        setUserEmail(response.data.email);
        setMessage('Token is valid. Please enter your new password.');
      } catch (error: any) {
        setError(error.response?.data?.detail || 'Invalid or expired reset link. Please request a new password reset.');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const getPasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthText = (strength: number): string => {
    if (strength < 2) return 'Weak';
    if (strength < 3) return 'Fair';
    if (strength < 4) return 'Good';
    return 'Strong';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const endpoints = getCurrentApiEndpoints();
      await axios.post(endpoints.RESET_PASSWORD, {
        token: token,
        new_password: password
      });
      
      setMessage('Password reset successfully! You can now log in with your new password.');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error: any) {
      setError(error.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <FormContainer>
        <Title>Verifying Reset Link</Title>
        <Description>Please wait while we verify your reset link...</Description>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Title>Reset Password</Title>
      <Description>
        {userEmail ? `Enter a new password for ${userEmail}` : 'Enter your new password'}
      </Description>
      
      {message && <SuccessMessage>{message}</SuccessMessage>}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <Form onSubmit={handleSubmit}>
        <InputGroup>
          <Label htmlFor="password">New Password</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your new password"
            required
            disabled={isLoading}
          />
          {password && (
            <PasswordStrength strength={getPasswordStrength(password)}>
              Password strength: {getPasswordStrengthText(getPasswordStrength(password))}
            </PasswordStrength>
          )}
        </InputGroup>
        
        <InputGroup>
          <Label htmlFor="confirmPassword">Confirm New Password</Label>
          <Input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your new password"
            required
            disabled={isLoading}
          />
        </InputGroup>
        
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </Form>
      
      <BackToLogin>
        <Link to="/login">Back to Login</Link>
      </BackToLogin>
    </FormContainer>
  );
};

export default ResetPassword;
